import React, { useState, useEffect, useRef } from 'react';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { ReactLenis } from 'lenis/react';

import layoutQuery from '../hooks/use-layout-query';
import '../assets/scss/main.scss';

import Provider from "../context/provider";
import { QuoteProvider } from '../context/QuoteContext';

import Header from '../components/header';
import Footer from '../components/footer';
import CookieBanner from '../components/CookieBanner';
// import GDPRCookieBanner from '../components/GDPRCookieBanner';

const Layout = ({ children, location, pageContext }) => {
  const data = layoutQuery();
  let { site } = pageContext;
  const { settings } = pageContext; // This is coming from gatsby-node.js

  // Set the site context to 'sister' if the page is /cart
  if (typeof window !== 'undefined') {
    if (window.location.pathname === '/cart/') {
      site = 'sister';
    } else if (window.location.pathname === '/sister/journal/') {
      site = 'sister'; 
    } else if (window.location.pathname === '/quote/') {
      site = 'sister'; // or any other logic you need for '/quote/'
    }
  }

  const [isFading, setIsFading] = useState(false);
  const [navigateTo, setNavigateTo] = useState(null);
  const [scrollDirection, setScrollDirection] = useState('none');
  const [scrolled, setScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const [quoteQuantity, setQuoteQuantity] = useState(0);

  const lenisRef = useRef();
  const lenis = useRef(null);

  // Enhanced onLenisInit function with better logging
  const onLenisInit = (lenisInstance) => {
    console.log("%c Lenis instance initialized!", "background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;");
    
    // Additional safety check
    if (!lenisInstance) {
      console.error("Warning: lenisInstance is null or undefined!");
      return;
    }
    
    // Store the instance in refs
    lenis.current = lenisInstance;
    window.__LENIS = lenisInstance;
    
    // Force scroll to top immediately upon initialization
    lenisInstance.scrollTo(0, { immediate: true });
    
    // Check if we need to reset scroll position (set by onRouteUpdate)
    if (window.__NEED_SCROLL_RESET) {
      console.log("Found pending scroll reset flag, executing now");
      window.scrollTo(0, 0);
      lenisInstance.scrollTo(0, { immediate: true });
      window.__NEED_SCROLL_RESET = false;
    }
    
    // Add a scroll lock that prevents scrolling until page is fully loaded
    lenisInstance.stop();
    
    // Re-enable scrolling after a short delay
    setTimeout(() => {
      lenisInstance.start();
      lenisInstance.resize();
    }, 300);
    
    console.log("Lenis instance stored and initialized");
  };

  // Enhanced route change handler
  useEffect(() => {
    console.log("Route changed to:", location.pathname);
    console.log("Lenis instance available:", !!lenis.current);
    
    if (lenis.current) {
      // Stop any ongoing scrolling
      lenis.current.stop();
      
      // Reset scroll position immediately with native method
      window.scrollTo(0, 0);
      
      // Then use Lenis for smooth scrolling with a small delay
      setTimeout(() => {
        lenis.current.start();
        lenis.current.scrollTo(0, { immediate: true });
      }, 50);
      
      // Add another check a bit later to ensure proper positioning
      setTimeout(() => {
        if (lenis.current) {
          lenis.current.scrollTo(0, { immediate: true });
        }
      }, 200);
    } else {
      console.warn("No Lenis instance available for scrolling");
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleLinkClick = (rawPath) => {
    if (typeof window !== 'undefined') {
      // Keep paths absolute so navigate() can't resolve them against the
      // current route and stack a segment on top of it.
      const path = /^(https?:)?\/\//.test(rawPath) ? rawPath : `/${String(rawPath || '').replace(/^\/+/, '')}`;

      if (path === location.pathname) {
        return;
      }
      
      // Immediately stop Lenis scrolling
      if (window.__LENIS) {
        window.__LENIS.stop();
      }
      
      // Force scroll to top immediately before starting fade transition
      window.scrollTo(0, 0);
      
      // If using Lenis, also tell it to scroll to top
      if (window.__LENIS) {
        window.__LENIS.scrollTo(0, { immediate: true });
      }
      
      // Set state to trigger fade transition
      setNavigateTo(path);
      setIsFading(true);
    }
  };

  useEffect(() => {
    if (isFading) {
      // Make sure we're at the top again just before navigation
      if (window.__LENIS) {
        window.__LENIS.scrollTo(0, { immediate: true });
      }
      window.scrollTo(0, 0);
      
      const timer = setTimeout(() => {
        navigate(navigateTo);
      }, 500); // Duration of the fade-out effect
      return () => clearTimeout(timer);
    }
  }, [isFading, navigateTo]);

  // When a new page loads, reset scroll position again
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Reset scroll position with native method immediately
      window.scrollTo(0, 0);
      
      if (window.__LENIS) {
        // First stop any ongoing scrolling
        window.__LENIS.stop();
        
        // Then reset position and restart after a small delay
        setTimeout(() => {
          window.__LENIS.start();
          window.__LENIS.scrollTo(0, { immediate: true });
        }, 50);
      }
    }
    
    setIsFading(false);
  }, [location.pathname]);

  useEffect(() => {
    const delay = setTimeout(() => {
      // Retrieve the 'quoteItems' from localStorage
      const storedQuoteItems = localStorage.getItem('quoteItems');
  
      console.log(storedQuoteItems);
  
      if (storedQuoteItems) {
        // Parse the JSON string into an array of quote items
        const quoteItems = JSON.parse(storedQuoteItems);
  
        // Calculate the total quantity of quote items
        const totalQuantity = quoteItems.length;
  
        // Set the total quantity in state
        setQuoteQuantity(totalQuantity);
  
        console.log(totalQuantity);
      }
    }, 100);
  
    // Clear the timeout if the component unmounts or changes
    return () => clearTimeout(delay);
  }, []);



  let lastScrollTop = 0; // Declare the variable using 'let'

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrolled = scrollTop >= 50;
    let direction = 'none';

    if (scrollTop >= 100) {
      if (scrollTop > lastScrollTop) {
        direction = 'down';
      } else {
        direction = 'up';
      }
    }

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

    setScrollDirection(direction);
    setScrolled(scrolled);
  };

  useEffect(() => {
    let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const headerClass = `${
    scrollDirection !== 'none' ? ` scrolled-${scrollDirection}` : ''
  }${scrolled ? ' scrolled' : ''}${isHovered ? ' scrolled-up' : ''}`;

  const isTradeApplication = location.pathname.includes('trade-application');

  return (
    <>
      <ReactLenis 
        key={location.pathname} // Add this line to force re-initialization on route change
        root 
        onLenisInstance={onLenisInit} 
        options={{
          duration: 1.2,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          direction: 'vertical',
          gestureDirection: 'vertical',
          smooth: true,
          smoothTouch: false,
          touchMultiplier: 2
        }}
      >
        <QuoteProvider>
          <Provider>
            {isTradeApplication ? (
              <button
                className="trade-application-close"
                onClick={() => navigate('/sister/trade/')}
                aria-label="Close"
              >
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.353516 0.353577L5.35352 5.35358M5.35352 5.35358L0.353516 10.3536M5.35352 5.35358L10.3535 10.3536M5.35352 5.35358L10.3535 0.353577" stroke="#F9F3F0"/>
                </svg>
              </button>
            ) : (
              <Header
                site={site}
                settings={settings}
                location={location}
                data={data}
                pageContext={pageContext}
                handleLinkClick={handleLinkClick}
                quoteQuantity={quoteQuantity}
              />
            )}
            <div className={`wrapper fade-wrapper ${isFading ? 'fadeout' : 'fadein'}`}>
              <main>
                {React.Children.map(children, (child) =>
                  React.cloneElement(child, { handleLinkClick, setQuoteQuantity, quoteQuantity })
                )}
              </main>
              {!isTradeApplication && (
                <Footer
                  site={site}
                  data={data}
                  handleLinkClick={handleLinkClick}
                />
              )}
            </div>
            <CookieBanner />
          </Provider>
        </QuoteProvider>
      </ReactLenis>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object,
  pageContext: PropTypes.object,
};

export default Layout;



// import React, { useState, useEffect, useRef } from 'react';
// import { navigate } from 'gatsby';
// import PropTypes from 'prop-types';
// import { ReactLenis } from 'lenis/react';

// import layoutQuery from '../hooks/use-layout-query';
// import '../assets/scss/main.scss';

// import Provider from "../context/provider";
// import { QuoteProvider } from '../context/QuoteContext';

// import Header from '../components/header';
// import Footer from '../components/footer';

// const Layout = ({ children, location, pageContext }) => {
//   const data = layoutQuery();
//   let { site } = pageContext;

//   // Set the site context to 'sister' if the page is /cart
//   if (typeof window !== 'undefined') {
//     if (window.location.pathname === '/cart/') {
//       site = 'sister';
//     } else if (window.location.pathname === '/sister/journal/') {
//       site = 'sister'; 
//     } else if (window.location.pathname === '/quote/') {
//       site = 'sister'; // or any other logic you need for '/quote/'
//     }
//   }
//   const [scrollDirection, setScrollDirection] = useState('none');
//   const [scrolled, setScrolled] = useState(false);
//   const [isFading, setIsFading] = useState(false);
//   const [isHovered, setIsHovered] = useState(false);
//   const [navigateTo, setNavigateTo] = useState(null);
//   const lenisRef = useRef();


//   let lastScrollTop = 0; // Declare the variable using 'let'

//   const handleScroll = () => {
//     const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
//     const scrolled = scrollTop >= 50;
//     let direction = 'none';

//     if (scrollTop >= 100) {
//       if (scrollTop > lastScrollTop) {
//         direction = 'down';
//       } else {
//         direction = 'up';
//       }
//     }

//     lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

//     setScrollDirection(direction);
//     setScrolled(scrolled);
//   };

//   const handleHeaderHover = () => {
//     setIsHovered((prevIsHovered) => !prevIsHovered);
//     setScrollDirection('none');
//   };

//   useEffect(() => {
//     let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
//     window.addEventListener('scroll', handleScroll);

//     return () => {
//       window.removeEventListener('scroll', handleScroll);
//     };
//   }, []);


//   const handleLinkClick = (path) => {
//     if (typeof window !== 'undefined') {
//       if (path === location.pathname) {
//         return;
//       }
//     }

//     setNavigateTo(path);
//     setIsFading(true);
//   };

//   useEffect(() => {
//     if (isFading) {
//       const timer = setTimeout(() => {
//         navigate(navigateTo);
//       }, 1000); // Duration of the fade-out effect
//       return () => clearTimeout(timer);
//     }
//   }, [isFading, navigateTo]);

//   useEffect(() => {
//     setIsFading(false);
//   }, [location.pathname]);

//   const scrollClass = `${
//     scrollDirection !== 'none' ? ` scrolled-${scrollDirection}` : ''
//   }${scrolled ? ' scrolled' : ''}${isHovered ? ' scrolled-up' : ''}`;


//   return (
//     <>
//       <ReactLenis root>
//         <QuoteProvider>
//           <Provider>
//             <Header
//               site={site}
//               location={location}
//               data={data}
//               pageContext={pageContext}
//               handleLinkClick={handleLinkClick}
//             />
//             <div className={`wrapper fade-wrapper ${isFading ? 'fadeout' : 'fadein'}`}>
//               <main className={`${scrollClass}`}>
//                 {React.Children.map(children, (child) =>
//                   React.cloneElement(child, { handleLinkClick })
//                 )}
//               </main>
//               <Footer 
//                 site={site}
//                 data={data}
//                 handleLinkClick={handleLinkClick}
//               />
//             </div>
//           </Provider>
//         </QuoteProvider>
//       </ReactLenis>
//     </>
//   );
// };

// Layout.propTypes = {
//   children: PropTypes.node.isRequired,
//   location: PropTypes.object,
//   pageContext: PropTypes.object,
// };

// export default Layout;
